import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { AlertTriangle, MapPin, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

// Skeleton loader for anomalies
const AnomaliesSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 w-64 bg-slate-200 rounded"></div>
    <div className="h-4 w-96 bg-slate-100 rounded"></div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => (
        <div key={`skeleton-${i}`} className="bg-slate-100 rounded-lg p-3 h-20"></div>
      ))}
    </div>
    <div className="bg-slate-100 rounded-lg h-64"></div>
  </div>
);

const SeasonalAnomalies = ({ onLocationClick }) => {
  const { API } = React.useContext(AppContext);
  const { filters } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedLocation, setExpandedLocation] = useState(null);

  const fetchAnomalies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.location) params.append('location', filters.location);
      if (filters.department) params.append('department', filters.department);
      if (filters.pay_types && filters.pay_types.length > 0) {
        filters.pay_types.forEach(pt => params.append('pay_types', pt));
      }
      
      const response = await axios.get(`${API}/analytics/anomalies?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching anomalies:', error);
      toast.error('Failed to load anomaly data');
    } finally {
      setLoading(false);
    }
  }, [API, filters.start_date, filters.end_date, filters.location, filters.department, filters.pay_types]);

  // Re-fetch when global filters change
  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  const getAnomalyColor = (variance_pct, is_anomaly) => {
    if (!is_anomaly) return '#4CAF93';  // Green for normal
    if (variance_pct > 50) return '#DC2626';  // Red for high severity
    return '#FF8C42';  // Orange for medium
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-[#1e3a5f] mb-2">{item.month_name || label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Premium Hours:</span>
              <span className="font-medium">{item.premium_hours?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Premium Cost:</span>
              <span className="font-medium text-[#4CAF93]">${item.premium_cost?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Variance:</span>
              <span className={`font-medium ${item.variance_pct > 0 ? 'text-[#FF8C42]' : 'text-[#2E5C8A]'}`}>
                {item.variance_pct > 0 ? '+' : ''}{item.variance_pct?.toFixed(1)}%
              </span>
            </div>
            {item.is_anomaly && (
              <div className="mt-2 pt-2 border-t">
                <span className="text-red-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Anomaly Detected
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <AnomaliesSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="seasonal-anomalies">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-[#FF8C42]" />
          Seasonal Overtime Anomalies
        </h3>
        <p className="text-sm text-muted-foreground">
          Periods with premium pay significantly above average (20%+ variance)
        </p>
      </div>

      {data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-border">
              <div className="text-2xl font-bold text-[#1e3a5f]">{data.summary?.total_anomalies || 0}</div>
              <div className="text-xs text-muted-foreground">Total Anomalies</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
              <div className="text-2xl font-bold text-red-600">{data.summary?.high_severity || 0}</div>
              <div className="text-xs text-red-600">High Severity</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-100">
              <div className="text-2xl font-bold text-[#FF8C42]">{data.summary?.locations_affected || 0}</div>
              <div className="text-xs text-[#FF8C42]">Locations Affected</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
              <div className="text-2xl font-bold text-[#2E5C8A]">{data.summary?.overall_avg_premium_hours?.toFixed(0) || 0}h</div>
              <div className="text-xs text-[#2E5C8A]">Avg Premium Hours</div>
            </div>
          </div>

          {/* Top Anomalies List */}
          {data.anomalies?.length > 0 && (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="bg-red-50 px-4 py-3 border-b border-red-100">
                <h4 className="font-semibold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Top Anomalies ({data.anomalies.length})
                </h4>
              </div>
              <div className="divide-y divide-border max-h-64 overflow-y-auto">
                {data.anomalies.slice(0, 10).map((anomaly) => (
                  <div 
                    key={`${anomaly.location}-${anomaly.year_month}`} 
                    className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => onLocationClick && onLocationClick(anomaly.location)}
                    data-testid={`anomaly-${anomaly.location}-${anomaly.year_month}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${anomaly.severity === 'high' ? 'bg-red-500' : 'bg-orange-500'}`}></div>
                      <div>
                        <div className="font-medium text-foreground">{anomaly.location}</div>
                        <div className="text-sm text-muted-foreground">{anomaly.month_name} {anomaly.year_month?.split('-')[0]}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-[#FF8C42]">+{anomaly.variance_pct?.toFixed(0)}%</div>
                      <div className="text-xs text-muted-foreground">{anomaly.premium_hours?.toFixed(0)}h premium</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Location Breakdown with Charts */}
          <div className="space-y-3">
            <h4 className="font-semibold text-[#1e3a5f]">By Location</h4>
            {data.by_location?.slice(0, 5).map((location, idx) => (
              <motion.div
                key={location.location}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-lg border border-border overflow-hidden"
              >
                {/* Location Header */}
                <button
                  onClick={() => setExpandedLocation(expandedLocation === location.location ? null : location.location)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-[#2E5C8A]" />
                    <div className="text-left">
                      <span className="font-medium text-foreground">{location.location}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        (Avg: {location.avg_premium_hours?.toFixed(0)}h/month)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {location.months?.some(m => m.is_anomaly) && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                        Has Anomalies
                      </span>
                    )}
                    {expandedLocation === location.location ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded Chart */}
                <AnimatePresence>
                  {expandedLocation === location.location && location.months?.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-border px-4 py-4"
                    >
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={location.months}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E8F0F8" />
                          <XAxis 
                            dataKey="month_name" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={(value) => value?.slice(0, 3)}
                          />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine 
                            y={location.avg_premium_hours} 
                            stroke="#2E5C8A" 
                            strokeDasharray="5 5"
                            label={{ value: 'Avg', position: 'right', fontSize: 10 }}
                          />
                          <Bar dataKey="premium_hours" name="Premium Hours">
                            {location.months.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={getAnomalyColor(entry.variance_pct, entry.is_anomaly)}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-4 mt-2 text-xs">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-[#4CAF93]"></div>
                          Normal
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-[#FF8C42]"></div>
                          Medium Anomaly
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-red-600"></div>
                          High Anomaly
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p>No anomaly data available</p>
        </div>
      )}
    </div>
  );
};

export default SeasonalAnomalies;
